import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, CalendarDays, X, Loader2, AlertCircle, RefreshCw, CheckCircle, Palmtree, Stethoscope, Award, Clock, Baby, HeartPulse, Briefcase, Home, Shield, Umbrella, BookOpen, Coffee, Plane, Zap } from "lucide-react";
import "../styles/leaveDashboard.css";
import MainLayout from "./MainLayout";
import CalendarPopup from "./CalendarPopup";
import { useAuth } from "../context/AuthContext";
import {
  getLeaveBalances,
  getLeavesByEmployee,
  getLeaveTypes,
  getLeavePolicies,
  cancelLeaveRequest,
  computeDynamicBalances,
  isWFH,
} from "../services/leaveApiService";

const TYPE_COLORS = {
  "Casual Leave": { color: "#4F6EF7", bg: "#EFF6FF", gradient: "linear-gradient(135deg, #4F6EF7 0%, #7C97FF 100%)", icon: Palmtree },
  "Casual/Sick": { color: "#10B981", bg: "#ECFDF5", gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)", icon: Stethoscope },
  "Sick Leave": { color: "#10B981", bg: "#ECFDF5", gradient: "linear-gradient(135deg, #10B981 0%, #6EE7B7 100%)", icon: HeartPulse },
  "Earned Leave": { color: "#8B5CF6", bg: "#F5F3FF", gradient: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)", icon: Award },
  "Permission": { color: "#F59E0B", bg: "#FFFBEB", gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)", icon: Clock },
  "Maternity Leave": { color: "#EC4899", bg: "#FDF2F8", gradient: "linear-gradient(135deg, #EC4899 0%, #F472B6 100%)", icon: Baby },
  "On Duty": { color: "#6366F1", bg: "#EEF2FF", gradient: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)", icon: Briefcase },
  "Work From Home": { color: "#0EA5E9", bg: "#F0F9FF", gradient: "linear-gradient(135deg, #0EA5E9 0%, #38BDF8 100%)", icon: Home },
  "Leave Without Pay": { color: "#EF4444", bg: "#FEF2F2", gradient: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", icon: Shield },
  "Paternity Leave": { color: "#14B8A6", bg: "#F0FDFA", gradient: "linear-gradient(135deg, #14B8A6 0%, #2DD4BF 100%)", icon: Baby },
  "Sabbatical Leave": { color: "#8B5CF6", bg: "#F5F3FF", gradient: "linear-gradient(135deg, #8B5CF6 0%, #C4B5FD 100%)", icon: BookOpen },
  "Compensatory Off": { color: "#F97316", bg: "#FFF7ED", gradient: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)", icon: Coffee },
  "Vacation Leave": { color: "#06B6D4", bg: "#ECFEFF", gradient: "linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)", icon: Plane },
};
const DEFAULT_COLOR = { color: "#6B7280", bg: "#F3F4F6", gradient: "linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)", icon: Zap };

const DAY_RANGES = ["All", "< 1 day", "1 day", "2–3 days", "4–7 days", "7+ days"];

function getDays(row) {
  const units = parseFloat(row.total_units || 0);
  return row.unit_type === "HOUR" ? units / 8 : units;
}

function matchDayRange(days, range) {
  if (range === "All")      return true;
  if (range === "< 1 day")  return days < 1;
  if (range === "1 day")    return days === 1;
  if (range === "2–3 days") return days >= 2 && days <= 3;
  if (range === "4–7 days") return days >= 4 && days <= 7;
  if (range === "7+ days")  return days > 7;
  return true;
}

const STATUS_CLASS = {
  PENDING: "status-pending",
  APPROVED_BY_LEAD: "status-approved",
  APPROVED: "status-approved",
  REJECTED: "status-rejected",
  CANCELLED: "status-rejected",
};

function statusLabel(status) {
  if (!status) return "";
  const s = String(status).toUpperCase();
  if (s === "APPROVED_BY_LEAD") return "Approved By Team Lead";
  // fallback: title case common statuses
  if (s === "PENDING") return "Pending";
  if (s === "APPROVED") return "Approved";
  if (s === "REJECTED") return "Rejected";
  if (s === "CANCELLED") return "Cancelled";
  return status;
}

function fmtDate(d) {
  if (!d) return "--";
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  const local = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  return `${String(local.getDate()).padStart(2, '0')}-${String(local.getMonth() + 1).padStart(2, '0')}-${local.getFullYear()}`;
}

function formatMonthYear(month, year) {
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const m = Number(month);
  if (!Number.isNaN(m) && m >= 1 && m <= 12) {
    return `${monthNames[m - 1]} ${year}`;
  }
  return month && year ? `${month} ${year}` : "";
}

// ── aggregateBalances removed in favor of computeDynamicBalances ──

// ── Toast Component ───────────────────────────────────
function Toast({ toast, onClose }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [toast, onClose]);

  if (!toast) return null;
  return (
    <div className={`db-toast db-toast--${toast.type}`}>
      {toast.type === "success"
        ? <CheckCircle size={15} />
        : <AlertCircle size={15} />
      }
      <span>{toast.message}</span>
      <button className="db-toast-close" onClick={onClose}><X size={13} /></button>
    </div>
  );
}

// ── Confirm Dialog Component ──────────────────────────
function ConfirmDialog({ show, reason, onReasonChange, onConfirm, onCancel }) {
  if (!show) return null;
  return (
    <div className="db-confirm-overlay">
      <div className="db-confirm-box">
        <div className="db-confirm-icon"><X size={22} /></div>
        <h4 className="db-confirm-title">Cancel Leave Request?</h4>
        <p className="db-confirm-msg">This action cannot be undone. The leave will be cancelled.</p>
        <textarea
          className="db-confirm-textarea"
          rows={4}
          placeholder="Enter cancellation reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
        />
        <div className="db-confirm-btns">
          <button className="db-confirm-no" onClick={onCancel}>No, Keep it</button>
          <button
            className="db-confirm-yes"
            onClick={onConfirm}
            disabled={!reason.trim()}
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyDashboardPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const empId = user?.employee_id ?? user?.employeeId ?? user?.empId ?? user?.id;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((p) => !p);

  const [search, setSearch] = useState("");
  const [balances, setBalances] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingBal, setLoadingBal] = useState(true);
  const [loadingHist, setLoadingHist] = useState(true);
  const [error, setError] = useState("");
  const [cancellingId, setCancellingId] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmId, setConfirmId] = useState(null); // which leave to cancel
  const [cancelReason, setCancelReason] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);

  // Filters State
  const [leaveType, setLeaveType] = useState("All");
  const [status, setStatus] = useState("All");
  const [dayRange, setDayRange] = useState("All");
  const [startFrom, setStartFrom] = useState("");
  const [startTo, setStartTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState([{ val: "All", label: "All Types" }]);
  const [statusOptions, setStatusOptions] = useState([
    { val: "All", label: "All Status" },
    { val: "PENDING", label: "Pending" },
    { val: "APPROVED", label: "Approved" },
    { val: "REJECTED", label: "Rejected" },
    { val: "CANCELLED", label: "Cancelled" }
  ]);

  const activeFilterCount = [
    leaveType !== "All",
    status !== "All",
    dayRange !== "All",
    !!startFrom,
    !!startTo
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch("");
    setLeaveType("All");
    setStatus("All");
    setDayRange("All");
    setStartFrom("");
    setStartTo("");
  };

  const showToast = (type, message) => setToast({ type, message });
  const closeToast = () => setToast(null);

  const fetchAll = useCallback(async () => {
    if (!empId) {
      setError("Employee ID not found. Please re-login.");
      setLoadingBal(false);
      setLoadingHist(false);
      setLoadingHolidays(false);
      return;
    }
    setError("");
    try {
      const raw = await getLeaveBalances(empId, token);
      const rows = await getLeavesByEmployee(empId, token);
      const types = await getLeaveTypes(token);
      const policies = await getLeavePolicies(token);
      
      setLeaveTypeOptions([
        { val: "All", label: "All Types" },
        ...types.filter(t => !isWFH(t)).map(t => ({ val: t.id || t.name, label: t.name || t.id }))
      ]);

      setHistory(rows);
      setBalances(computeDynamicBalances(raw, rows, types, policies));

      const holRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/holidays`);
      if (holRes.ok) {
        const holData = await holRes.json();
        setHolidays(holData);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingBal(false);
      setLoadingHist(false);
      setLoadingHolidays(false);
    }
  }, [empId, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Step 1: click Cancel → show confirm dialog
  const handleCancelClick = (id) => {
    setConfirmId(id);
    setCancelReason("");
  };

  // Step 2: user clicks "Yes" in dialog → API call
  const handleConfirmCancel = async () => {
    if (!confirmId) return;
    if (!cancelReason.trim()) {
      showToast("error", "Please provide a reason for cancellation.");
      return;
    }

    const id = confirmId;
    setConfirmId(null);
    setCancellingId(id);
    try {
      await cancelLeaveRequest(id, empId, token, cancelReason.trim());
      setHistory((prev) =>
        prev.map((h) => h.id === id ? { ...h, status: "CANCELLED" } : h)
      );
      const raw = await getLeaveBalances(empId, token);
      const rows = await getLeavesByEmployee(empId, token);
      const types = await getLeaveTypes(token);
      setBalances(computeDynamicBalances(raw, rows, types));
      showToast("success", "Leave request cancelled successfully.");
    } catch (e) {
      showToast("error", e.message || "Failed to cancel leave. Please try again.");
    } finally {
      setCancellingId(null);
    }
  };

  const filteredHistory = history.filter((h) => {
    const q = search.toLowerCase();
    const searchMatch = !q || (h.leave_type_name || "").toLowerCase().includes(q) || (h.status || "").toLowerCase().includes(q);
    
    const typeMatch = leaveType === "All" || h.leave_type_name === leaveType || h.leave_type_id === leaveType;
    const statusMatch = status === "All" || (h.status || "").toUpperCase() === status;
    const daysMatch = matchDayRange(getDays(h), dayRange);
    
    let dateMatch = true;
    if (startFrom || startTo) {
      const sDate = new Date(h.start_date);
      if (startFrom) dateMatch = dateMatch && sDate >= new Date(startFrom);
      if (startTo) dateMatch = dateMatch && sDate <= new Date(startTo);
    }
    
    return searchMatch && typeMatch && statusMatch && daysMatch && dateMatch;
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const upcomingHolidays = holidays
    .filter((h) => new Date(h.holiday_date) >= today)
    .sort((a, b) => new Date(a.holiday_date) - new Date(b.holiday_date))
    .slice(0, 5);

  return (
    <>
      <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} title="My Leave Dashboard" subtitle="Manage your leaves and view your balance">

        <div className="db-wrap" style={{ padding: '0 0 20px 0' }}>
          <main className="db-content">

            {error && (
              <div className="db-error-banner">
                <AlertCircle size={15} /> {error}
              </div>
            )}

            {/* Leave Balances — Card Grid */}
            <section className="db-balances-section">


              {loadingBal ? (
                <div className="db-loading">
                  <Loader2 size={20} className="db-spin" /> Loading balances…
                </div>
              ) : balances.length === 0 ? (
                <div className="db-empty-state">No leave balances found.</div>
              ) : (
                <div className="db-leave-cards-grid">
                  {balances.map((lb) => {
                    const style = TYPE_COLORS[lb.leave_type_name] || DEFAULT_COLOR;
                    const IconComp = style.icon;
                    const unit = lb.unit_type === "HOUR" ? "Hours" : "Days";
                    const available = parseFloat(lb.available).toFixed(lb.unit_type === "HOUR" ? 1 : 0);
                    const used = lb.used;

                    return (
                      <div
                        className="db-leave-card"
                        key={lb.leave_type_id}
                        onClick={() => navigate(`/LeaveForm?leaveType=${lb.leave_type_id}`)}
                        style={{ '--card-color': style.color, '--card-bg': style.bg, '--card-gradient': style.gradient }}
                      >
                        <div className="db-leave-card__header">
                          <div className="db-leave-card__icon-wrap">
                            <IconComp size={18} />
                          </div>
                          <h3 className="db-leave-card__title">{lb.leave_type_name}</h3>
                        </div>
                        <div className="db-leave-card__stats">
                          <div className="db-leave-card__stat">
                            <span className="db-leave-card__stat-label">Available</span>
                            <span className="db-leave-card__stat-value db-leave-card__stat-value--available">{available}</span>
                            <span className="db-leave-card__stat-unit">{unit}</span>
                          </div>
                          <div className="db-leave-card__divider" />
                          <div className="db-leave-card__stat">
                            <span className="db-leave-card__stat-label">Applied</span>
                            <span className="db-leave-card__stat-value db-leave-card__stat-value--booked">{used}</span>
                            <span className="db-leave-card__stat-unit">{unit}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Bottom Grid */}
            <div className="db-bottom-grid">

              {/* Leave History */}
              <div className="db-history-card">
                <div className="db-history-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h3 style={{ margin: 0 }}>Leave History</h3>
                    <div className="db-search-wrap" style={{ margin: 0 }}>
                      <Search size={14} className="db-search-icon" />
                      <input
                        className="db-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search leave history..."
                      />
                    </div>
                    <button onClick={() => setFiltersOpen(true)} style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 14px', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#334155' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg> Filter {activeFilterCount > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>{activeFilterCount}</span>}
                    </button>
                    {activeFilterCount > 0 && (
                      <button onClick={resetFilters} style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#dc2626', cursor: 'pointer', fontWeight: 500 }}>
                        <RefreshCw size={12} /> Reset
                      </button>
                    )}
                  </div>
                  
                  <div className="db-topbar-right" style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: 0 }}>
                    <button className="db-apply-btn" onClick={() => navigate("/LeaveForm")} style={{ margin: 0 }}>
                      <CalendarDays size={15} /> Apply Leave
                    </button>
                  </div>
                </div>

                {filtersOpen && (
                  <>
                    <div className="lh-modal-backdrop" onClick={() => setFiltersOpen(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 999 }} />
                    <div style={{ position: 'fixed', top: 0, right: 0, width: '340px', height: '100vh', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)', zIndex: 1000, display: 'flex', flexDirection: 'column', animation: 'slideInRight 0.3s ease' }}>
                      <style>{`
                        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
                      `}</style>
                      <div style={{ padding: '24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>Filters</h3>
                        <button onClick={() => setFiltersOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
                      </div>
                      
                      <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        
                        {/* Status */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Status</label>
                          <select value={status} onChange={e => setStatus(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                            {statusOptions.map((opt) => (
                              <option key={opt.val} value={opt.val}>{opt.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Leave Type */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Leave Type</label>
                          <select value={leaveType} onChange={e => setLeaveType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                            {leaveTypeOptions.map((lt) => (
                              <option key={lt.val} value={lt.val}>{lt.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Duration */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Duration</label>
                          <select value={dayRange} onChange={e => setDayRange(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc' }}>
                            {DAY_RANGES.map((dr) => (
                              <option key={dr} value={dr}>{dr}</option>
                            ))}
                          </select>
                        </div>

                        {/* Date Range */}
                        <div>
                          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>Date Range</label>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>From</label>
                              <input type="date" value={startFrom} max={startTo || undefined} onChange={e => setStartFrom(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ display: 'block', fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>To</label>
                              <input type="date" value={startTo} min={startFrom || undefined} onChange={e => setStartTo(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', backgroundColor: '#f8fafc' }} />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ padding: '20px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px' }}>
                        <button onClick={resetFilters} style={{ flex: 1, padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fff', border: '1px solid #fee2e2', borderRadius: '8px', gap: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>
                          <RotateCcw size={14} /> Reset
                        </button>
                        <button onClick={() => setFiltersOpen(false)} style={{ flex: 1, padding: '10px', background: '#3b82f6', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                          Apply
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {loadingHist ? (
                  <div className="db-loading"><Loader2 size={16} className="db-spin" /> Loading history…</div>
                ) : filteredHistory.length === 0 ? (
                  <div className="db-empty-state">No leave requests found.</div>
                ) : (
                  <div className="db-table-wrap">
                    <table className="db-table">
                      <thead>
                        <tr>
                          {["Leave Type", "Start Date", "End Date", "Applied On", "Units", "Type", "Status", "Action"].map(
                            (col) => <th key={col}>{col}</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredHistory.map((row) => {
                          const canCancel = row.status?.toUpperCase() === "PENDING";
                          const units = row.unit_type === "HOUR"
                            ? `${row.total_units} hrs`
                            : `${row.total_units} days`;
                          return (
                            <tr key={row.id}>
                              <td className="col-bold">{row.leave_type_name}</td>
                              <td className="col-muted">{fmtDate(row.start_date)}</td>
                              <td className="col-muted">{fmtDate(row.end_date)}</td>
                              <td className="col-muted">{fmtDate(row.created_at)}</td>
                              <td className="col-muted">{units}</td>
                              <td>
                                <div className="db-type-cell">
                                  <span className="db-reqtype-badge">
                                    {row.request_type || "FULL_DAY"}
                                  </span>
                                  {(row.start_time || row.end_time) && (
                                    <span className="db-time-range">
                                      {row.start_time ? row.start_time.slice(0, 5) : "--"}
                                      {" → "}
                                      {row.end_time ? row.end_time.slice(0, 5) : "--"}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className={`db-status-badge ${STATUS_CLASS[row.status?.toUpperCase()] || ""}`}>
                                  {statusLabel(row.status)}
                                </span>
                              </td>
                              <td>
                                {canCancel ? (
                                  <button
                                    className="db-cancel-btn"
                                    onClick={() => handleCancelClick(row.id)}
                                    disabled={cancellingId === row.id}
                                  >
                                    {cancellingId === row.id
                                      ? <Loader2 size={11} className="db-spin" />
                                      : <X size={12} />}
                                    {cancellingId === row.id ? "…" : "Cancel"}
                                  </button>
                                ) : (
                                  <span className="col-muted">--</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Upcoming */}
              <div className="db-upcoming-card">
                <h3>Upcoming Holidays</h3>
                {loadingHolidays ? (
                  <div className="db-loading"><Loader2 size={16} className="db-spin" /> Loading…</div>
                ) : upcomingHolidays.length === 0 ? (
                  <div className="db-upcoming-empty">
                    <CalendarDays size={40} className="db-empty-icon" />
                    <p>No upcoming holidays scheduled</p>
                  </div>
                ) : (
                  <div className="db-upcoming-list">
                    {upcomingHolidays.map((h) => (
                      <div className="db-upcoming-item" key={h.id}>
                        <div
                          className="db-upcoming-dot"
                          style={{ background: h.is_optional ? "#F59E0B" : "#10B981" }}
                        />
                        <div>
                          <p className="db-upcoming-type">{h.name}</p>
                          <p className="db-upcoming-dates">
                            {fmtDate(h.holiday_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </MainLayout>

      {/* Toast */}
      <Toast toast={toast} onClose={closeToast} />

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={!!confirmId}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onConfirm={handleConfirmCancel}
        onCancel={() => {
          setConfirmId(null);
          setCancelReason("");
        }}
      />

      <CalendarPopup
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        mode="leave"
        holidays={holidays}
        leaves={history}
      />
    </>
  );
}